// src/main.jsx
// Force Supabase env validation at startup (before React render)
import "./supabaseClient";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

// Defensive localization guard (run first to avoid early crashes)
import "./lib/localizationGuard";

import App from "./App.jsx";
import "./index.css";

// Providers
import { StorageProvider } from "@/contexts/StorageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { HelpProvider } from "@/contexts/HelpContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { EntitlementsProvider } from "@/contexts/EntitlementsContext";
import { AccessProvider } from "@/contexts/AccessContext";
import { UnreadCountProvider } from "@/contexts/UnreadCountContext";

// Leaflet CSS for map components
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

console.log("ENV VITE_GOOGLE_CLIENT_ID =", import.meta.env.VITE_GOOGLE_CLIENT_ID);

// ---- Install global listeners only once (Vite HMR-safe) ----
const MAIN_GUARD_KEY = "__mudoMainListenersInstalled_v1";

function installGlobalListenersOnce() {
  if (window[MAIN_GUARD_KEY]) return;
  window[MAIN_GUARD_KEY] = true;

  const onLoad = () => {
    console.log("GIS loaded?", !!window.google?.accounts?.oauth2);
  };

  window.addEventListener("load", onLoad);

  // DEV-only extension noise suppression
  let ignoredExtensionErrorLogged = false;

  const shouldIgnoreExtensionError = (message, stack) => {
    const msg = String(message || "");
    const stk = String(stack || "");
    return (
      /Could not establish connection\. Receiving end does not exist\./i.test(msg) ||
      /RegisterClientLocalizationsError/i.test(msg) ||
      /Unchecked runtime\.lastError/i.test(msg) ||
      /message port closed before a response was received/i.test(msg) ||
      /chrome-extension:\/\//i.test(stk) ||
      /moz-extension:\/\//i.test(stk) ||
      /safari-extension:\/\//i.test(stk)
    );
  };

  const onError = (event) => {
    if (shouldIgnoreExtensionError(event?.message, event?.error?.stack)) {
      if (!ignoredExtensionErrorLogged) {
        console.warn("[dev] Ignored browser-extension error:", event?.message);
        ignoredExtensionErrorLogged = true;
      }
      event.preventDefault();
    }
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    const message = reason?.message || reason;
    if (shouldIgnoreExtensionError(message, reason?.stack)) {
      if (!ignoredExtensionErrorLogged) {
        console.warn("[dev] Ignored browser-extension rejection:", message);
        ignoredExtensionErrorLogged = true;
      }
      event.preventDefault();
    }
  };

  if (import.meta.env.DEV) {
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
  }

  // Save references so we can remove on HMR dispose
  window.__mudoMainListenerRefs_v1 = { onLoad, onError, onUnhandledRejection };
}

installGlobalListenersOnce();

// Clean up on Vite HMR dispose (prevents stacking listeners)
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    const refs = window.__mudoMainListenerRefs_v1;
    if (refs?.onLoad) window.removeEventListener("load", refs.onLoad);
    if (refs?.onError) window.removeEventListener("error", refs.onError);
    if (refs?.onUnhandledRejection)
      window.removeEventListener("unhandledrejection", refs.onUnhandledRejection);

    delete window.__mudoMainListenerRefs_v1;
    delete window[MAIN_GUARD_KEY];
  });
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <BrowserRouter>
    <AuthProvider>
      <EntitlementsProvider>
        <AccessProvider>
          <StorageProvider>
            <NotificationsProvider>
              <UnreadCountProvider>
                <HelpProvider>
                  <App />
                </HelpProvider>
              </UnreadCountProvider>
            </NotificationsProvider>
          </StorageProvider>
        </AccessProvider>
      </EntitlementsProvider>
    </AuthProvider>
  </BrowserRouter>
);
